/* eslint-disable @typescript-eslint/no-unsafe-argument */
import cx from 'classnames';
import Cookies from 'js-cookie';
import React, { useEffect, useState } from 'react';
import {
  MFE_FEATURE_BRANCH_COOKIE,
  MFE_DEV_OVERRIDE_COOKIE,
  OPD_DEV_OVERRIDE_COOKIE,
  OPD_FEATURE_BRANCH_COOKIE
} from '|/hooks/use-global-mfe-config';
import styles from './control-panel.module.css';

type MfeSource = 'testazure' | 'localhost' | 'feature-branch';

interface MfePanelConfig {
  label: string;
  devOverrideCookie: string;
  featureBranchCookie: string;
  analyticsPrefix: string;
}

const MFE_PANELS: MfePanelConfig[] = [
  {
    label: 'Verifx MFE',
    devOverrideCookie: MFE_DEV_OVERRIDE_COOKIE,
    featureBranchCookie: MFE_FEATURE_BRANCH_COOKIE,
    analyticsPrefix: 'pp_devpanel_mfe'
  },
  {
    label: 'OPD MFE',
    devOverrideCookie: OPD_DEV_OVERRIDE_COOKIE,
    featureBranchCookie: OPD_FEATURE_BRANCH_COOKIE,
    analyticsPrefix: 'pp_devpanel_opd_mfe'
  }
];

const getInitialSource = (config: MfePanelConfig): MfeSource => {
  if (Cookies.get(config.devOverrideCookie) === 'true') return 'localhost';
  if (Cookies.get(config.featureBranchCookie)) return 'feature-branch';
  return 'testazure';
};

const MfePanel: React.FC<{ config: MfePanelConfig }> = ({ config }) => {
  const [showInterface, setShowInterface] = useState(false);
  const [mfeSource, setMfeSource] = useState<MfeSource>('testazure');
  const [featureBranchName, setFeatureBranchName] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setMfeSource(getInitialSource(config));
    setFeatureBranchName(String(Cookies.get(config.featureBranchCookie) ?? ''));
    setIsLoading(false);
  }, [config]);

  if (isLoading) {
    return null;
  }

  const isOverrideActive = mfeSource !== 'testazure';

  const handleSourceChange = (source: MfeSource) => {
    setMfeSource(source);

    Cookies.remove(config.devOverrideCookie);
    Cookies.remove(config.featureBranchCookie);
    setFeatureBranchName('');

    if (source === 'localhost') {
      Cookies.set(config.devOverrideCookie, 'true');
      window.location.reload();
    } else if (source === 'testazure') {
      window.location.reload();
    }
  };

  return (
    <div className={styles.wrapper}>
      <button
        className={cx(styles.tab, showInterface && styles.expanded)}
        onClick={() => {
          setShowInterface(!showInterface);
        }}
        data-analytics-id={`${config.analyticsPrefix}_override`}
      >
        {isOverrideActive && <span className={styles.tabIndicatorDot} />}
        {config.label}
      </button>

      {showInterface && (
        <div className={styles.content}>
          <div className={styles.experimentGroup}>Load MFE from:</div>
          <label className={styles.radio}>
            <input
              type="radio"
              name={`${config.analyticsPrefix}-source`}
              checked={mfeSource === 'testazure'}
              onChange={() => {
                handleSourceChange('testazure');
              }}
              data-analytics-id={`${config.analyticsPrefix}_testazure`}
            />
            testazure
          </label>
          <label className={styles.radio}>
            <input
              type="radio"
              name={`${config.analyticsPrefix}-source`}
              checked={mfeSource === 'localhost'}
              onChange={() => {
                handleSourceChange('localhost');
              }}
              data-analytics-id={`${config.analyticsPrefix}_localhost`}
            />
            localhost
          </label>
          <label className={styles.radio}>
            <input
              type="radio"
              name={`${config.analyticsPrefix}-source`}
              checked={mfeSource === 'feature-branch'}
              onChange={() => {
                setMfeSource('feature-branch');
              }}
              data-analytics-id={`${config.analyticsPrefix}_feature_branch`}
            />
            feature branch
          </label>
          {mfeSource === 'feature-branch' && (
            <div className={styles.input}>
              <label htmlFor={`${config.analyticsPrefix}-feature-branch`}>Branch name:</label>
              <input
                id={`${config.analyticsPrefix}-feature-branch`}
                type="text"
                value={featureBranchName}
                autoComplete="none"
                spellCheck="false"
                autoCapitalize="none"
                className={styles.input}
                data-analytics-id={`${config.analyticsPrefix}_feature_branch_input`}
                onChange={e => {
                  setFeatureBranchName(e.target.value);
                }}
              />
              <button
                data-analytics-id={`${config.analyticsPrefix}_feature_branch_reload`}
                className={styles.refreshBtn}
                disabled={!featureBranchName}
                onClick={() => {
                  Cookies.remove(config.devOverrideCookie);
                  Cookies.set(config.featureBranchCookie, featureBranchName);
                  window.location.reload();
                }}
              >
                Reload Page
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MfeDevOverride = () => {
  const isProduction = process.env.NODE_ENV === 'production';

  if (isProduction) {
    return null;
  }

  return (
    <>
      {MFE_PANELS.map(config => (
        <MfePanel key={config.label} config={config} />
      ))}
    </>
  );
};

export default MfeDevOverride;
